import * as T from 'three';
import type { RideTrack } from './model';
import type { RideCourse } from './course';
import { BIOMES } from './biomes';
import { batchStatic, tube } from './bike';

export function raceLineLayout(track: RideTrack, course: RideCourse) {
  const start = course.sample(0), finish = course.sample(track.length);
  const shared = Math.hypot(start.x - finish.x, start.y - finish.y, start.z - finish.z) < 1;
  return { start, finish, shared, postOffset: track.width / 2 + 1, stripeDepth: track.width / Math.ceil(track.width / 2) };
}

/** Each square follows the course. Its leading edge ends at the scored finish distance. */
export function finishStripeTiles(track: RideTrack, course: RideCourse) {
  const columns = Math.ceil(track.width / 2) * 2, cell = track.width / columns;
  return Array.from({ length: columns * 2 }, (_, i) => {
    const row = Math.floor(i / columns), column = i % columns;
    const points = [
      [track.length - (2 - row) * cell, -track.width / 2 + column * cell],
      [track.length - (2 - row) * cell, -track.width / 2 + (column + 1) * cell],
      [track.length - (1 - row) * cell, -track.width / 2 + (column + 1) * cell],
      [track.length - (1 - row) * cell, -track.width / 2 + column * cell],
    ].map(([d, offset]) => { const p = course.sample(d); return [p.x - p.tz * offset, p.y + .15, p.z + p.tx * offset]; });
    return { white: (row + column) % 2 === 0, points };
  });
}

export function makeRaceLines(track: RideTrack, course: RideCourse, height: (x: number, z: number) => number) {
  const group = new T.Group(); group.name = 'race-lines';
  const layout = raceLineLayout(track, course), theme = BIOMES[track.biome];
  // Powder-coated aluminium truss, glossy painted panels and a satin track mat.
  const dark = new T.MeshPhysicalMaterial({ color: '#1f2a2e', roughness: .42, metalness: .55, clearcoat: .4, clearcoatRoughness: .3 });
  const white = new T.MeshPhysicalMaterial({ color: '#f4efe4', roughness: .5, clearcoat: .5, clearcoatRoughness: .35 });
  const accent = new T.MeshPhysicalMaterial({ color: theme.accent, roughness: .45, clearcoat: .6, clearcoatRoughness: .25 });
  const box = (parent: T.Group, size: [number, number, number], position: [number, number, number], material: T.Material) => {
    const mesh = new T.Mesh(new T.BoxGeometry(...size), material); mesh.position.set(...position); mesh.castShadow = true; parent.add(mesh); return mesh;
  };
  function sign(text: string, width: number, h: number, color: string, checker = false) {
    const canvas = document.createElement('canvas'); canvas.width = 1536; canvas.height = 256;
    const ctx = canvas.getContext('2d')!; ctx.fillStyle = color; ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (checker) for (const edge of [0, 1344]) for (let row = 0; row < 4; row++) for (let col = 0; col < 3; col++) {
      ctx.fillStyle = (row + col) % 2 ? '#fff7e5' : '#24383e'; ctx.fillRect(edge + col * 64, row * 64, 64, 64);
    }
    ctx.fillStyle = '#182b31'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = `italic 900 ${text.length > 10 ? 125 : 174}px Arial`; ctx.fillText(text, 768, 115, checker ? 1080 : 1400);
    ctx.font = 'bold 29px Arial'; ctx.fillText('HARTEX  /  TREAD QUEST', 768, 220);
    const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace; texture.anisotropy = 4;
    const material = new T.MeshPhysicalMaterial({ map: texture, roughness: .55, clearcoat: .35, clearcoatRoughness: .4 });
    const panel = new T.Group(); box(panel, [width, h, .28], [0, 0, 0], dark);
    for (const side of [-1, 1]) {
      const face = new T.Mesh(new T.PlaneGeometry(width, h), material); face.position.z = side * .15; face.rotation.y = side === -1 ? Math.PI : 0; panel.add(face);
    }
    return panel;
  }
  function gantry(distance: number, label: string, finish: boolean) {
    const p = course.sample(distance), gate = new T.Group(); gate.name = finish ? 'finish-gantry' : 'start-gantry';
    gate.position.set(p.x, p.y, p.z); gate.rotation.y = Math.atan2(p.tx, p.tz);
    gate.userData = { distance, label, postOffset: layout.postOffset, clearWidth: track.width };
    for (const side of [-1, 1]) {
      const x = side * layout.postOffset;
      box(gate, [.6, 6, .6], [x, 3, 0], dark); box(gate, [1.15, .3, 1.4], [x, .1, 0], dark);
      for (let j = 0; j < 6; j++) box(gate, [.63, .45, .63], [x, .7 + j * .72, 0], j % 2 ? white : accent);
    }
    const banner = sign(label, track.width + 2.6, 1.7, finish ? theme.accent : '#f48e7b', finish); banner.position.y = 5.5; gate.add(banner);
    box(gate, [track.width + 2.8, .12, .4], [0, 6.42, 0], white);
    group.add(gate);
  }
  if (!layout.shared) gantry(0, 'START', false);
  gantry(track.length, layout.shared ? 'START / FINISH' : 'FINISH', true);
  const stripe = new T.Group(); stripe.name = 'finish-stripe'; stripe.userData = { endDistance: track.length, depth: layout.stripeDepth };
  for (const tile of finishStripeTiles(track, course)) {
    const geo = new T.BufferGeometry(); geo.setAttribute('position', new T.Float32BufferAttribute(tile.points.flat(), 3)); geo.setIndex([0, 1, 2, 0, 2, 3]); geo.computeVertexNormals();
    // Course right vectors make these faces point up; no floating, flat decals on steep trails.
    stripe.add(new T.Mesh(geo, tile.white ? white : dark));
  }
  batchStatic(stripe); group.add(stripe);
  const chute = new T.Group(); chute.name = 'finish-chute';
  for (const side of [-1, 1]) {
    for (let before = 4; before <= 60; before += 4) {
      const a = course.sample(track.length - before), b = course.sample(track.length - before + 4), offset = side * (track.width / 2 + 1.25);
      const pa: [number,number,number] = [a.x - a.tz * offset, a.y, a.z + a.tx * offset], pb: [number,number,number] = [b.x - b.tz * offset, b.y, b.z + b.tx * offset];
      box(chute, [.12, 1.3, .12], [pa[0], pa[1] + .65, pa[2]], dark);
      for (const h of [.45, 1.15]) chute.add(tube([pa[0],pa[1]+h,pa[2]], [pb[0],pb[1]+h,pb[2]], .045, h > 1 ? accent : white));
      if (before % 12 === 0) {
        const flag = new T.Group(); flag.position.set(...pa); flag.rotation.y = Math.atan2(a.tx, a.tz);
        box(flag, [.09, 4, .09], [0, 2, 0], dark); box(flag, [.8, 1.9, .05], [side * .43, 2.9, 0], before % 24 ? accent : white); chute.add(flag);
      }
    }
  }
  for (const before of [100, 50]) {
    const p = course.sample(track.length - before), board = sign(`${before} M`, 2.8, 1.4, theme.accent);
    board.position.set(p.x + p.tz * (track.width / 2 + 2.7), p.y + 2.5, p.z - p.tx * (track.width / 2 + 2.7)); board.rotation.y = Math.atan2(p.tx, p.tz); chute.add(board);
    box(chute, [.15, 2.8, .15], [board.position.x, p.y + 1.4, board.position.z], dark);
  }
  batchStatic(chute); group.add(chute);
  if (!layout.shared) {
    // A visible run-out makes an open downhill trail continue beyond the timing line.
    const p = layout.finish, vertices: number[] = [], indices: number[] = [];
    for (let i = 0; i <= 25; i++) for (const side of [-1, 1]) {
      const x = p.x + p.tx * i - p.tz * side * (track.width / 2 + 1), z = p.z + p.tz * i + p.tx * side * (track.width / 2 + 1);
      const blend = Math.min(1, i / 12), y = p.y * (1 - blend) + (height(x, z) + .25) * blend;
      vertices.push(x, y + .045, z);
      if (i < 25 && side === -1) { const j = i * 2; indices.push(j, j + 1, j + 2, j + 1, j + 3, j + 2); }
    }
    const geo = new T.BufferGeometry(); geo.setAttribute('position', new T.Float32BufferAttribute(vertices, 3)); geo.setIndex(indices); geo.computeVertexNormals();
    const runoff = new T.Mesh(geo, new T.MeshStandardMaterial({color:theme.shoulder,side:T.DoubleSide,roughness:1})); runoff.name = 'finish-runout'; runoff.receiveShadow = true; group.add(runoff);
  }
  return group;
}
